import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { mdiDraw } from '@mdi/js';
import { CircularProgress, Typography } from '@mui/material';
import { type MUIDataTableColumnDef } from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAppMessaging } from '../../../hooks/useAppMessaging';
import { getDateString } from '../../../utils/date';
import { getUrlParam } from '../../../utils/url';
import { LaunchToBreakoutRoomsDialog } from '../../common/breakoutRooms/LaunchToBreakoutRoomsDialog';
import { DeleteConfirmationDialog } from '../../common/dialog/DeleteConfirmationDialog';
import { ActionMenu } from '../../common/table/ActionMenu';
import { APIDataTable, type DataTableProvider } from '../../common/table/APIDataTable';
import { paginateAndSort } from '../../common/table/utils';
import { StyledLoadingButton } from '../../forms/LoadingButton';
import MainLayout from '../../layout/MainLayout';
import { UserRoles } from '../../roster/types';
import { WhiteboardContext } from '../context/WhiteboardContext';
import { SettingsDialog } from '../SettingsDialog';
import { type Session, SessionTypes } from '../types';
import { MISSING_SESSION_ID, STALE_SESSION_STATE } from '../whiteboard-errors';
import WhiteboardError from '../WhiteboardError';

export const WhiteboardSessions: React.FC = () => {
    const { ready, t } = useTranslation('whiteboard');
    const { t: tCommon } = useTranslation('common');

    const { getSession, getSessions, saveSession, copySession, deleteSession, openWhiteboardTab } =
        useContext(WhiteboardContext);

    const [loading, setLoading] = useState<boolean>(true);
    const [savingSession, setSavingSession] = useState<boolean>(false);
    const [launchingSession, setLaunchingSession] = useState<Session>();
    const [launchingToBreakoutRooms, setLaunchingToBreakoutRooms] = useState<boolean>(false);
    const [currentSession, setCurrentSession] = useState<Session | null>(null);
    const [deletingSession, setDeletingSession] = useState<Session | null>(null);

    const sessionsRef = useRef<Session[]>([]);

    const [settingsDialogOpen, setSettingsDialogOpen] = useState<boolean>(false);
    const [breakoutRoomsDialogOpen, setBreakoutRoomsDialogOpen] = useState<boolean>(false);
    const [tableKey, setTableKey] = useState<number>(0);

    const { classUuid } = useParams<{ classUuid: string }>();
    const { closeWindow } = useAppMessaging();

    const currentBreakoutName = getUrlParam('breakout_name') || '';
    const allBreakoutNames: string[] = useMemo(() => {
        const param = getUrlParam('breakout_names') as string;
        return param ? param.split(',').sort((a, b) => a.localeCompare(b, undefined, { numeric: true })) : [];
    }, []);

    const handleSave = async (session: Session) => {
        setSavingSession(true);
        await saveSession(session);
        setSettingsDialogOpen(false);
        setTableKey((prev) => prev + 1); // Force table reload.
        setSavingSession(false);
    };

    const handleLaunch = useCallback(
        async (session: Session) => {
            setLaunchingSession(session);
            if (!session.session_id) {
                throw new WhiteboardError(`${MISSING_SESSION_ID}: Cannot launch a session without a session_id`);
            }

            if (currentBreakoutName) {
                // Instructor is in a breakout room, launch immediately for this room only.
                await openWhiteboardTab(session, [currentBreakoutName]);
                closeWindow();
            } else if (allBreakoutNames.length < 1) {
                // No breakout rooms, launch for main room.
                await openWhiteboardTab(session);
                closeWindow();
            } else {
                // Multiple breakout rooms available and instructor is in main room. Ask instructor to choose rooms.
                setCurrentSession(session);
                setBreakoutRoomsDialogOpen(true);
            }
        },
        [allBreakoutNames.length, closeWindow, currentBreakoutName, openWhiteboardTab]
    );

    const handleSaveAndLaunch = async (session: Session) => {
        setSavingSession(true);
        const savedSession = await saveSession(session);
        setTableKey((prev) => prev + 1); // Force table reload.
        if (savedSession === true) {
            await handleLaunch(session);
        } else if (savedSession) {
            await handleLaunch(savedSession);
        }
    };

    const handleLaunchToBreakoutRooms = async ({ breakoutRooms }) => {
        if (!currentSession) {
            return;
        }

        setLaunchingSession(currentSession);
        setLaunchingToBreakoutRooms(true);
        await openWhiteboardTab(currentSession, breakoutRooms);
        closeWindow();
    };

    const handleCancelLaunchToBreakoutRooms = () => {
        setBreakoutRoomsDialogOpen(false);
        setLaunchingToBreakoutRooms(false);
        setLaunchingSession(undefined);
        setSavingSession(false);
    };

    const handleDelete = useCallback(
        async (session?: Session) => {
            if (session) {
                await deleteSession(session.session_id);
                setDeletingSession(null);
                setTableKey((prev) => prev + 1);
            }
        },
        [deleteSession]
    );

    const controls = useMemo(() => {
        const newSession = () => {
            setCurrentSession({
                name: '',
                class_id: classUuid,
                type: SessionTypes.Shared,
                presentation_access: UserRoles.Assistant
            } as Session);
            setSettingsDialogOpen(true);
        };

        return [{ label: t('createWhiteboard'), onClick: newSession }];
    }, [classUuid, t]);

    const columns: MUIDataTableColumnDef[] = useMemo(() => {
        // Future implementation.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleRowCopy = async (sessionId: string) => {
            setLoading(true);
            const session = await getSession(sessionId);
            if (session) {
                if (session.data?.presenter && Object.keys(session.data?.presenter).length === 0) {
                    delete session.data.presenter;
                }
                await copySession(session);
                setTableKey((prev) => prev + 1);
            }
        };
        const handleRowEdit = async (sessionId: string) => {
            setCurrentSession(null);
            setSettingsDialogOpen(true);
            const session = await getSession(sessionId);
            if (session) {
                if (session.data?.presenter && Object.keys(session.data?.presenter).length === 0) {
                    delete session.data.presenter;
                }
            }
            setCurrentSession(session as Session);
        };
        const handleRowDelete = async (sessionId: string) => {
            setLoading(true);
            const session = await getSession(sessionId);
            setLoading(false);
            setDeletingSession(session as Session);
        };
        const handleRowLaunch = async (sessionId: string) => {
            setCurrentSession(null);
            const session = sessionsRef.current.find((s) => s.session_id === sessionId);
            if (!session) {
                // This shouldn't happen unless there's a logic issue.
                throw new WhiteboardError(`${STALE_SESSION_STATE}: Stale session state.`);
            }

            await handleLaunch(session);
        };
        return [
            {
                name: 'session_id',
                options: {
                    display: 'excluded'
                }
            },
            {
                name: 'name',
                label: t('whiteboard')
            },
            {
                name: 'type',
                label: t('type'),
                options: {
                    customBodyRender: (value) => {
                        if (value === SessionTypes.Shared) {
                            return t('shared');
                        }
                        if (value === SessionTypes.Presentation) {
                            return t('presentation');
                        }
                        if (value === SessionTypes.Breakout) {
                            return t('breakout');
                        }
                        return t('individual');
                    }
                }
            },
            {
                name: 'updated_at',
                label: t('lastEdited'),
                options: {
                    sortDirection: 'desc',
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    customBodyRender: (value, tableMeta) => {
                        if (value) {
                            return getDateString(new Date(value));
                        }
                    }
                }
            },
            {
                name: 'actions',
                label: tCommon('actions'),
                options: {
                    sort: false,
                    searchable: false,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    customBodyRender: (value, tableMeta) => {
                        const sessionId = tableMeta.rowData[0];
                        const actions = [
                            // { name: tCommon('copy'), onClick: () => handleRowCopy(sessionId) },
                            { name: tCommon('settings'), onClick: () => handleRowEdit(sessionId) },
                            { name: tCommon('delete'), onClick: () => handleRowDelete(sessionId) }
                        ];
                        return <ActionMenu uniqueId={sessionId} title={tCommon('actions')} actions={actions} />;
                    }
                }
            },
            {
                name: 'launch',
                label: t('launch'),
                options: {
                    sort: false,
                    searchable: false,
                    customBodyRender: (value, tableMeta) => {
                        const sessionId = tableMeta.rowData[0];
                        const session = sessionsRef.current.find((s) => s.session_id === sessionId);
                        if (session) {
                            return (
                                <StyledLoadingButton
                                    color="primary"
                                    variant="outlined"
                                    loading={launchingSession === session}
                                    disabled={launchingSession && launchingSession !== session}
                                    onClick={() => handleRowLaunch(sessionId)}
                                >
                                    {t('launch')}
                                </StyledLoadingButton>
                            );
                        }
                    }
                }
            }
        ];
    }, [copySession, getSession, handleLaunch, launchingSession, t, tCommon]);

    const sessionDataProvider: DataTableProvider = useCallback(
        async (tableState) => {
            setLoading(true);
            const sessions = await getSessions();
            sessionsRef.current = sessions;

            const [data, count] = paginateAndSort(sessions, tableState);

            setLoading(false);
            return { data, count };
        },
        [getSessions]
    );

    const renderSessions = () => (
        <MainLayout icon={mdiDraw} title={t('whiteboard')} controls={controls} loading={loading}>
            <APIDataTable
                key={tableKey}
                columns={columns}
                dataProvider={sessionDataProvider}
                extraOptions={{
                    filter: false,
                    search: false,
                    viewColumns: false,
                    download: false,
                    print: false,
                    setTableProps: () => ({
                        size: 'small'
                    }),
                    selectableRowsHideCheckboxes: true
                }}
            />
            <SettingsDialog
                open={settingsDialogOpen}
                loading={!currentSession}
                saving={savingSession}
                onClose={() => setSettingsDialogOpen(false)}
                session={currentSession}
                onSave={handleSave}
                onSaveAndLaunch={handleSaveAndLaunch}
            />
            <LaunchToBreakoutRoomsDialog
                title={t('launchWhiteboard')}
                open={breakoutRoomsDialogOpen}
                launching={!!launchingToBreakoutRooms}
                onClose={handleCancelLaunchToBreakoutRooms}
                breakoutRooms={allBreakoutNames}
                onLaunch={handleLaunchToBreakoutRooms}
            />
            {deletingSession && (
                <DeleteConfirmationDialog
                    title={tCommon('deleteConfirm', { name: t('whiteboard') })}
                    open={!!deletingSession}
                    value={deletingSession}
                    onConfirm={handleDelete}
                    onClose={() => setDeletingSession(null)}
                >
                    <Typography>{tCommon('deleteConfirmMessage', { name: deleteSession.name })}</Typography>
                </DeleteConfirmationDialog>
            )}
        </MainLayout>
    );

    return ready ? renderSessions() : <CircularProgress />;
};
